import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useOutletContext, useSearchParams } from "react-router-dom";
import EnquiryModal from "./EnquiryModal/EnquiryModal";
import { useTheme } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material";
import { Icons } from "./icons.jsx";
import {
  Pill,
  ActionButton,
  GuruAvatar,
  StarRow,
  IconBtn,
} from "./primitives.jsx";
import TabBar from "./TabBar.jsx";
import TopicAccordion from "./TopicAccordion.jsx";
import TopicChip from "./TopicChip.jsx";
import {
  useSkillsAcademyProgramDetail,
  useStartSkillsAcademyDemoSimulation,
  useIsSubscribed,
} from "./useSkillsAcademy.js";
import {
  InfoBackButton,
  InfoBackWrap,
  InfoHeroSection,
  InfoHeroOverlay,
  InfoVideoBox,
  InfoVideoImg,
  InfoVideoShimmer,
  InfoChatPill,
  InfoPlayBtn,
  InfoColumn,
  InfoPillRow,
  InfoHeroTitle,
  InfoHeroBlurb,
  InfoGuruCard,
  InfoGuruName,
  InfoGuruRole,
  InfoStatsRow,
  InfoStatItem,
  InfoActionsRow,
  InfoOutcomeBanner,
  InfoOutcomeBox,
  InfoOutcomeTitle,
  InfoTabSection,
  InfoOverviewTabWrap,
  InfoOverviewGrid,
  InfoLearnCard,
  InfoLearnCardHeader,
  InfoLearnIconBox,
  InfoLearnTitle,
  InfoLearnGrid,
  InfoLearnItem,
  InfoLearnItemText,
  InfoTopicsCard,
  InfoTopicsCardHeader,
  InfoTopicsIconBox,
  InfoTopicsTitle,
  InfoTopicsChipWrap,
  InfoAboutCard,
  InfoAboutTitle,
  InfoAboutText,
  InfoTopicsTabWrap,
  InfoTopicsTabTitle,
  InfoTopicsTabDesc,
  InfoAccordionWrap,
  InfoRatingSidebar,
  InfoRatingBig,
  InfoRatingNumber,
  InfoRatingCount,
  InfoDivider,
  InfoBreakdownList,
  InfoBreakdownRow,
  InfoBreakdownStar,
  InfoBreakdownBarWrap,
  InfoBreakdownBar,
  InfoBreakdownPct,
  InfoMentionsLabel,
  InfoMentionsRow,
  InfoMentionChip,
  InfoReviewList,
  InfoReviewCard,
  InfoReviewHeader,
  InfoReviewInitials,
  InfoReviewMeta,
  InfoReviewMetaTop,
  InfoReviewerName,
  InfoReviewVerifiedBadge,
  InfoReviewCompletedBadge,
  InfoReviewTime,
  InfoReviewText,
  InfoLoadMoreBtn,
  InfoGuruImageWrap,
  InfoGuruImage,
  InfoGuruImageOverlay,
  InfoCredentialsList,
  InfoCredentialsLabel,
  InfoCredentialItem,
  InfoCredentialText,
  InfoGuroBioCol,
  InfoGuroBioName,
  InfoGuroBioRole,
  InfoGuroBioPara,
  InfoFinalCTA,
  InfoFinalCTATitle,
  InfoFinalCTASubtitle,
  InfoFinalCTAActions,
  InfoOutcomeLearners,
  InfoGuruLeftCol,
  InfoLearnCheckSvg,
  IconBtnGhostWhite,
  InfoStatStrong,
  InfoGuroBioStartWrap,
} from "./style.js";

import { DashLoader } from "../DashLoader/DashLoader";

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const days = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (days < 1) return "today";
  if (days < 7) return `${days} day${days !== 1 ? "s" : ""} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks !== 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  if (months < 13) return `${months} month${months !== 1 ? "s" : ""} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years !== 1 ? "s" : ""} ago`;
}

const TOP_MENTIONS = [];

// ── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "topics", label: "Topics" },
  { id: "reviews", label: "Reviews" },
  { id: "guru", label: "About Your Guru" },
];

// ── ProgramInfoPage ───────────────────────────────────────────────────────────
const ProgramInfoPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { training_program_id } = useParams();
  const [searchParams] = useSearchParams();
  const { onSimulationStarted } = useOutletContext();
  const [tab, setTab] = useState("overview");
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [showAllLearnPoints, setShowAllLearnPoints] = useState(false);
  const chatMutation = useStartSkillsAcademyDemoSimulation();
  const enrolled = useIsSubscribed(training_program_id);

  const handleChatClick = () => {
    if (chatMutation.isPending) return;
    chatMutation.mutate(program?.id, {
      onSuccess: (data) => onSimulationStarted?.(data),
    });
  };
  const [visibleCount, setVisibleCount] = useState(4);

  // Fetch full detail (includes reviews + chapters)
  const { data: detail, isLoading: detailLoading } =
    useSkillsAcademyProgramDetail(training_program_id);

  // Reviews from API, or empty while loading
  const allReviews = Array.isArray(detail?.reviews) ? detail.reviews : [];
  const visibleReviews = allReviews.slice(0, visibleCount);

  // Compute star breakdown from actual reviews
  const STAR_BREAKDOWN = [5, 4, 3, 2, 1].map((star) => {
    const count = allReviews.filter((r) => r.star_rating === star).length;
    const pct =
      allReviews.length > 0 ? Math.round((count / allReviews.length) * 100) : 0;
    return { star, pct };
  });

  const nextScenario = useMemo(() => {
    if (!detail?.chapters) return null;
    let first = null;
    for (const chapter of detail.chapters) {
      for (const scenario of chapter.scenarios ?? []) {
        if (!first) first = scenario;
        if (!scenario.completed) return scenario;
      }
    }
    return first;
  }, [detail]);

  useEffect(() => {
    if (searchParams.get("autostart") && !detailLoading && nextScenario) {
      navigate(`/dashboard/simulate/${nextScenario.id}`, { replace: true });
    }
  }, [searchParams, detailLoading, nextScenario, navigate]);

  if (detailLoading || !detail) {
    return <DashLoader text="Loading Program Details..." />;
  }

  const program = detail;
  const handleContinue = () => {
    if (nextScenario) navigate(`/dashboard/simulate/${nextScenario.id}`);
  };
  const [accentStart, accentEnd] = program.accent ?? ["#01ECFF", "#962EFF"];

  React.useEffect(() => {
    document.title = `${program.name} - Skills Academy | GuruNow`;
  }, [program]);

  return (
    <div className="mp-info-wrap">
      <InfoBackWrap>
        <InfoBackButton onClick={() => navigate(-1)}>
          ← Back to Skills Academy
        </InfoBackButton>
      </InfoBackWrap>

      {/* Hero */}
      <InfoHeroSection
        className="mp-hero-section"
        $bg={`linear-gradient(135deg,${accentStart}cc 0%,${accentEnd}cc 60%,${accentEnd}aa 100%)`}
      >
        <InfoHeroOverlay />
        <div className="mp-hero-grid">
          {/* Avatar video box */}
          <InfoVideoBox>
            <InfoVideoImg
              src={program.default_avatar_persona?.preview_image || ""}
              alt=""
            />
            <InfoVideoShimmer />
            {/* Brand-aligned "Chat with" pill */}
            <InfoChatPill>
              <span>Chat with {program.guru?.full_name.split(" ")[0]}</span>
            </InfoChatPill>
            {/* Play → opens guru chat */}
            <InfoPlayBtn
              onClick={handleChatClick}
              disabled={chatMutation.isPending}
            >
              {chatMutation.isPending ? (
                <CircularProgress size={24} style={{ color: "#fff" }} />
              ) : (
                <Icons.play size={32} style={{ color: "#fff" }} />
              )}
            </InfoPlayBtn>
          </InfoVideoBox>

          {/* Info column */}
          <InfoColumn>
            <InfoPillRow>
              <Pill tone="light">{program.categories?.[0]?.name ?? ""}</Pill>
            </InfoPillRow>
            <InfoHeroTitle className="mp-hero-title">
              {program.name}
            </InfoHeroTitle>
            <InfoHeroBlurb>
              {program.description || program.short_description || ""}
            </InfoHeroBlurb>
            <InfoGuruCard>
              <GuruAvatar
                src={program.guru?.photo}
                name={program.guru?.full_name}
                size={48}
                ring="rgba(255,255,255,0.7)"
              />
              <div>
                <InfoGuruName>Hosted by {program.guru?.full_name}</InfoGuruName>
                <InfoGuruRole>{program.guru?.role}</InfoGuruRole>
              </div>
            </InfoGuruCard>
            <InfoStatsRow>
              {[
                [<Icons.clock size={16} />, program.duration, "total"],
                [
                  <Icons.book size={16} />,
                  program.lesson_count,
                  "Skill Development Sessions",
                ],
                [
                  <Icons.users size={16} style={{ color: "currentColor" }} />,
                  program.sim_count,
                  "Simulation Challenges",
                ],
                [<Icons.star size={16} />, program.rating, ""],
              ].map(([icon, val, lbl], i) => (
                <InfoStatItem key={i}>
                  {icon}
                  <span>
                    <InfoStatStrong>{val}</InfoStatStrong> {lbl}
                  </span>
                </InfoStatItem>
              ))}
            </InfoStatsRow>
            <InfoActionsRow>
              {enrolled ? (
                <ActionButton
                  variant="primary"
                  size="lg"
                  icon={<Icons.arrowRight size={18} />}
                  onClick={handleContinue}
                >
                  Continue Learning
                </ActionButton>
              ) : (
                <ActionButton
                  variant="primary"
                  size="lg"
                  icon={<Icons.zap size={18} />}
                  onClick={() => setEnquiryOpen(true)}
                >
                  Start for Free
                </ActionButton>
              )}
              {/* {program?.guru && program?.demo_scenario_id && (
                <ActionButton
                  variant="ghost"
                  size="lg"
                  icon={
                    chatMutation.isPending ? (
                      <CircularProgress size={16} style={{ color: "#fff" }} />
                    ) : (
                      <Icons.mic size={18} />
                    )
                  }
                  onClick={handleChatClick}
                  disabled={chatMutation.isPending}
                >
                  {chatMutation.isPending
                    ? "Connecting…"
                    : `Chat with ${program.guru.full_name?.split(" ")[0]}`}
                </ActionButton>
              )} */}
              {/* <IconBtnGhostWhite title="Share">
                <Icons.share size={18} />
              </IconBtnGhostWhite>
              <IconBtnGhostWhite title="Save">
                <Icons.bookmark size={18} />
              </IconBtnGhostWhite> */}
            </InfoActionsRow>
          </InfoColumn>
        </div>
      </InfoHeroSection>

      {/* Outcome banner */}
      <InfoOutcomeBanner>
        <InfoOutcomeBox>
          <Icons.zap size={24} style={{ color: "#01ECFF" }} />
          <div>
            <InfoOutcomeTitle>{program.featured_stat_text}</InfoOutcomeTitle>
            <InfoOutcomeLearners>
              Loved by{" "}
              {program.learner_count
                ? `${program.learner_count.toLocaleString()}+`
                : "0"}{" "}
              learners
            </InfoOutcomeLearners>
          </div>
        </InfoOutcomeBox>
      </InfoOutcomeBanner>

      {/* Tab navigation */}
      <InfoTabSection>
        <TabBar tabs={TABS} active={tab} onChange={setTab} />

        {/* ── Overview ── */}
        {tab === "overview" && (
          <InfoOverviewTabWrap>
            {/* Top row: What you'll learn + Topics Covered */}
            <InfoOverviewGrid>
              {/* What you'll learn */}
              <InfoLearnCard>
                <InfoLearnCardHeader>
                  <InfoLearnIconBox>
                    {/* lightbulb-05.svg icon */}
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#lb-clip)">
                        <path
                          d="M8.00016 1.33331V1.99998M2.00016 7.99998H1.3335M3.66683 3.66665L3.26676 3.26658M12.3335 3.66665L12.7337 3.26658M14.6668 7.99998H14.0002M6.66683 8.99998H9.3335M8.00016 8.99998V12.3333M10.3335 11.2493C11.3429 10.5232 12.0002 9.33833 12.0002 7.99998C12.0002 5.79084 10.2093 3.99998 8.00016 3.99998C5.79102 3.99998 4.00016 5.79084 4.00016 7.99998C4.00016 9.33833 4.65745 10.5232 5.66683 11.2493V12.5333C5.66683 13.28 5.66683 13.6534 5.81215 13.9386C5.93999 14.1895 6.14396 14.3935 6.39484 14.5213C6.68006 14.6666 7.05343 14.6666 7.80016 14.6666H8.20016C8.9469 14.6666 9.32027 14.6666 9.60548 14.5213C9.85637 14.3935 10.0603 14.1895 10.1882 13.9386C10.3335 13.6534 10.3335 13.28 10.3335 12.5333V11.2493Z"
                          stroke="#01ECFF"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="lb-clip">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </InfoLearnIconBox>
                  <InfoLearnTitle>What You'll Learn</InfoLearnTitle>
                </InfoLearnCardHeader>
                <InfoLearnGrid>
                  {(showAllLearnPoints
                    ? (program.learning_points ?? [])
                    : (program.learning_points ?? []).slice(0, 6)
                  ).map((point) => (
                    <InfoLearnItem key={point}>
                      <InfoLearnCheckSvg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="9"
                          cy="9"
                          r="8"
                          stroke="#01ECFF"
                          strokeWidth="1.4"
                        />
                        <path
                          d="M5.5 9L7.8 11.3L12.5 6.7"
                          stroke="#01ECFF"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </InfoLearnCheckSvg>
                      <InfoLearnItemText>{point}</InfoLearnItemText>
                    </InfoLearnItem>
                  ))}
                </InfoLearnGrid>
                {(program.learning_points ?? []).length > 6 && (
                  <button
                    onClick={() => setShowAllLearnPoints((v) => !v)}
                    style={{
                      marginTop: 12,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#01ECFF",
                      fontSize: 13,
                      fontWeight: 500,
                      padding: 0,
                    }}
                  >
                    {showAllLearnPoints
                      ? "Show Less ↑"
                      : `Show More (${(program.learning_points ?? []).length - 6} more) ↓`}
                  </button>
                )}
              </InfoLearnCard>

              {/* Topics Covered */}
              <InfoTopicsCard>
                <InfoTopicsCardHeader>
                  <InfoTopicsIconBox>
                    {/* list.svg icon */}
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 7.99998L6 7.99998M14 3.99998L6 3.99998M14 12L6 12M3.33333 7.99998C3.33333 8.36817 3.03486 8.66665 2.66667 8.66665C2.29848 8.66665 2 8.36817 2 7.99998C2 7.63179 2.29848 7.33331 2.66667 7.33331C3.03486 7.33331 3.33333 7.63179 3.33333 7.99998ZM3.33333 3.99998C3.33333 4.36817 3.03486 4.66665 2.66667 4.66665C2.29848 4.66665 2 4.36817 2 3.99998C2 3.63179 2.29848 3.33331 2.66667 3.33331C3.03486 3.33331 3.33333 3.63179 3.33333 3.99998ZM3.33333 12C3.33333 12.3682 3.03486 12.6666 2.66667 12.6666C2.29848 12.6666 2 12.3682 2 12C2 11.6318 2.29848 11.3333 2.66667 11.3333C3.03486 11.3333 3.33333 11.6318 3.33333 12Z"
                        stroke="#A87EFF"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </InfoTopicsIconBox>
                  <InfoTopicsTitle>Topics Covered</InfoTopicsTitle>
                </InfoTopicsCardHeader>
                <InfoTopicsChipWrap>
                  {(program.categories ?? []).map((t) => (
                    <TopicChip key={t.name} topic={t} />
                  ))}
                </InfoTopicsChipWrap>
              </InfoTopicsCard>
            </InfoOverviewGrid>

            {/* Bottom row: About this program (full width) */}
            <InfoAboutCard>
              <InfoAboutTitle>About This Program</InfoAboutTitle>
              <InfoAboutText>
                {program.long_description || program.description || ""}
              </InfoAboutText>
            </InfoAboutCard>
          </InfoOverviewTabWrap>
        )}

        {/* ── Topics ── */}
        {tab === "topics" && (
          <div>
            <InfoTopicsTabWrap>
              <InfoTopicsTabTitle>Topics you'll practise</InfoTopicsTabTitle>
              <InfoTopicsTabDesc>
                Each topic includes a skill development session and a simulation
                challenge with {program.guru?.full_name.split(" ")[0]}.
              </InfoTopicsTabDesc>
            </InfoTopicsTabWrap>
            <InfoAccordionWrap>
              <TopicAccordion topics={program.chapters ?? []} />
            </InfoAccordionWrap>
          </div>
        )}

        {/* ── Reviews ── */}
        {tab === "reviews" && (
          <div className="mp-reviews-layout">
            {/* ── Left: rating sidebar ── */}
            <InfoRatingSidebar>
              {/* Big rating + stars */}
              <InfoRatingBig>
                <InfoRatingNumber>{program.rating}</InfoRatingNumber>
                <StarRow value={program.rating} size={20} />
                <InfoRatingCount>
                  {(program.review_count ?? 0).toLocaleString()} reviews
                </InfoRatingCount>
              </InfoRatingBig>

              <InfoDivider />

              {/* Star breakdown */}
              <InfoBreakdownList>
                {STAR_BREAKDOWN.map(({ star, pct }) => (
                  <InfoBreakdownRow key={star}>
                    <InfoBreakdownStar>{star}</InfoBreakdownStar>
                    <Icons.star size={13} />
                    <InfoBreakdownBarWrap>
                      <InfoBreakdownBar $pct={pct} />
                    </InfoBreakdownBarWrap>
                    <InfoBreakdownPct>{pct}%</InfoBreakdownPct>
                  </InfoBreakdownRow>
                ))}
              </InfoBreakdownList>

              <InfoDivider />

              {/* Top mentions */}
              <div>
                <InfoMentionsLabel>Top Mentions</InfoMentionsLabel>
                <InfoMentionsRow>
                  {TOP_MENTIONS.map((m) => (
                    <InfoMentionChip key={m}>{m}</InfoMentionChip>
                  ))}
                </InfoMentionsRow>
              </div>
            </InfoRatingSidebar>

            {/* ── Right: review cards + load more ── */}
            <InfoReviewList>
              {visibleReviews.map((r, i) => (
                <InfoReviewCard key={i}>
                  {/* Card header */}
                  <InfoReviewHeader>
                    {/* Initials avatar */}
                    {r.reviewer_avatar ? (
                      <img
                        src={r.reviewer_avatar}
                        alt={r.reviewer_name}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          flexShrink: 0,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <InfoReviewInitials>
                        {(r.reviewer_name || "?")
                          .split(" ")
                          .map((word) => word[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </InfoReviewInitials>
                    )}

                    {/* Name, badges, time */}
                    <InfoReviewMeta>
                      <InfoReviewMetaTop>
                        <InfoReviewerName>{r.reviewer_name}</InfoReviewerName>
                        {true && (
                          <InfoReviewVerifiedBadge>
                            <Icons.check size={11} /> Verified
                          </InfoReviewVerifiedBadge>
                        )}
                        {true && (
                          <InfoReviewCompletedBadge>
                            <Icons.award size={11} /> Completed
                          </InfoReviewCompletedBadge>
                        )}
                      </InfoReviewMetaTop>
                      <InfoReviewTime>
                        {formatTimeAgo(r.created_at)}
                      </InfoReviewTime>
                    </InfoReviewMeta>

                    {/* Stars right-aligned */}
                    <StarRow value={r.star_rating} size={16} />
                  </InfoReviewHeader>

                  {/* Review text */}
                  <InfoReviewText>{r.review_text}</InfoReviewText>
                </InfoReviewCard>
              ))}

              {/* Load more */}
              {visibleCount < allReviews.length && (
                <InfoLoadMoreBtn onClick={() => setVisibleCount((n) => n + 4)}>
                  Load more reviews
                </InfoLoadMoreBtn>
              )}
            </InfoReviewList>
          </div>
        )}

        {/* ── About Guru ── */}
        {tab === "guru" && (
          <div className="mp-about-layout">
            {/* Left col: photo + credentials */}
            <InfoGuruLeftCol
              sx={{ "@media(max-width:639px)": { display: "contents" } }}
            >
              <InfoGuruImageWrap
                sx={{ "@media(max-width:639px)": { order: 1 } }}
              >
                <InfoGuruImage
                  src={program.guru?.photo}
                  alt={program.guru?.full_name}
                />
                <InfoGuruImageOverlay $b={accentEnd} />
              </InfoGuruImageWrap>
              {program.guru?.credentials && (
                <InfoCredentialsList
                  sx={{ "@media(max-width:639px)": { order: 3 } }}
                >
                  <InfoCredentialsLabel>Credentials</InfoCredentialsLabel>
                  {program.guru?.credentials.map((c) => (
                    <InfoCredentialItem key={c}>
                      <Icons.award
                        size={14}
                        style={{ color: "#A87EFF", flexShrink: 0 }}
                      />
                      <InfoCredentialText>{c}</InfoCredentialText>
                    </InfoCredentialItem>
                  ))}
                </InfoCredentialsList>
              )}
            </InfoGuruLeftCol>

            {/* Right col: name+role, bio, CTA */}
            <InfoGuroBioCol
              sx={{ "@media(max-width:639px)": { display: "contents" } }}
            >
              <Box sx={{ "@media(max-width:639px)": { order: 2 } }}>
                <InfoGuroBioName>{program.guru?.full_name}</InfoGuroBioName>
                <InfoGuroBioRole>{program.guru?.role}</InfoGuroBioRole>
              </Box>
              <Box sx={{ "@media(max-width:639px)": { order: 4 } }}>
                {(program.guru?.bio || "").split("\n\n").map((para, i) => (
                  <InfoGuroBioPara key={i}>{para}</InfoGuroBioPara>
                ))}
                <InfoGuroBioStartWrap>
                  {enrolled ? (
                    <ActionButton
                      variant="primary"
                      size="md"
                      icon={<Icons.arrowRight size={16} />}
                      onClick={handleContinue}
                    >
                      Continue Learning
                    </ActionButton>
                  ) : (
                    <ActionButton
                      variant="primary"
                      size="md"
                      icon={<Icons.zap size={16} />}
                      onClick={() => setEnquiryOpen(true)}
                    >
                      Start for Free
                    </ActionButton>
                  )}
                </InfoGuroBioStartWrap>
              </Box>
            </InfoGuroBioCol>
          </div>
        )}
      </InfoTabSection>

      {/* Final CTA */}
      <InfoFinalCTA>
        <div>
          <InfoFinalCTATitle>Ready to actually try it?</InfoFinalCTATitle>
          <InfoFinalCTASubtitle>
            Spend 5 minutes in a real scenario. Decide for yourself.
          </InfoFinalCTASubtitle>
        </div>
        <InfoFinalCTAActions>
          {enrolled ? (
            <ActionButton
              variant="primary"
              size="lg"
              icon={<Icons.arrowRight size={18} />}
              onClick={handleContinue}
            >
              Continue Learning
            </ActionButton>
          ) : (
            <ActionButton
              variant="primary"
              size="lg"
              icon={<Icons.zap size={18} />}
              onClick={() => setEnquiryOpen(true)}
            >
              Start for Free
            </ActionButton>
          )}
        </InfoFinalCTAActions>
      </InfoFinalCTA>

      {enquiryOpen && (
        <EnquiryModal program={program} onClose={() => setEnquiryOpen(false)} />
      )}
    </div>
  );
};

export default ProgramInfoPage;
